/**
 * TRYING A COLOUR WITHOUT ASKING ANYBODY.
 *
 * Choosing the accent has cost this project most of a day: a hex is proposed,
 * the app is rebuilt, a screenshot is taken, it is looked at, and the answer
 * comes back an hour later. That loop is the problem, not the indecision - a
 * color is a thing you judge in half a second and then want to change again.
 *
 * So the palette is readable from the URL. Add ?accent=1b4332 and the whole
 * product wears it: buttons, the selected row, the marks on the figure cards,
 * the progress bar, the mark in the sidebar. It survives a reload, because it
 * is remembered, and ?accent=reset puts it back.
 *
 * WHAT IT DELIBERATELY CANNOT DO. It sets four values and nothing else. It
 * cannot change a status color, because amber means unfinished work and rose
 * means money owed everywhere in this product and a theme that could break
 * that would be a theme that could make the app lie. And it never touches the
 * night render or the bulb colors: those are the product, not the chrome.
 */

/** The four things a person actually wants to try. */
export interface Theme {
  /** What you press, and what has been agreed. */
  accent: string
  /** The ink on top of the accent. Worked out, unless it is given. */
  accentInk: string
  /** The surface green: a selected row, a fill nothing is read through. */
  mint: string
  /** The strong version of it, for a bar with nothing on top. */
  mintStrong: string
}

const HEX = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i

/** #abc and abcdef both mean something; anything else means nothing. */
export function normalizeHex(input: string): string | null {
  const v = input.trim()
  if (!HEX.test(v)) return null
  const h = v.replace('#', '').toLowerCase()
  return `#${h.length === 3 ? h.split('').map((c) => c + c).join('') : h}`
}

/** Perceived lightness, so the ink on a button is chosen rather than guessed. */
export function luminance(hex: string): number {
  const h = normalizeHex(hex)
  if (!h) return 0
  const n = (i: number) => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16) / 255
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  return 0.2126 * lin(n(0)) + 0.7152 * lin(n(1)) + 0.0722 * lin(n(2))
}

/**
 * BLACK OR WHITE ON TOP, WHICHEVER CAN BE READ.
 *
 * This is the whole reason a color picker is safe to hand over. Half the
 * greens tried today needed near black on them and half needed white, and
 * getting that wrong is what made a button unreadable rather than merely
 * ugly. 0.42 is where the two contrasts cross.
 */
export const inkOn = (hex: string): string => (luminance(hex) > 0.42 ? '#08110c' : '#ffffff')

/** A lighter version of a color, for the surface that carries no text. */
export function lighten(hex: string, amount: number): string {
  const h = normalizeHex(hex)
  if (!h) return hex
  const ch = (i: number) => {
    const v = parseInt(h.slice(1 + i * 2, 3 + i * 2), 16)
    return Math.round(v + (255 - v) * amount)
  }
  const to = (v: number) => v.toString(16).padStart(2, '0')
  return `#${to(ch(0))}${to(ch(1))}${to(ch(2))}`
}

export const DEFAULT_THEME: Theme = {
  accent: '#1b4332',
  accentInk: '#ffffff',
  mint: '#86efac',
  mintStrong: '#4ade80',
}

const KEY = 'lightsos.theme.v1'

/** What the URL is asking for, if anything. */
export function themeFromSearch(search: string): Partial<Theme> | 'reset' | null {
  const q = new URLSearchParams(search)
  const accent = q.get('accent')
  const mint = q.get('mint')
  if (accent === 'reset' || mint === 'reset') return 'reset'
  const out: Partial<Theme> = {}
  const a = accent ? normalizeHex(accent) : null
  const m = mint ? normalizeHex(mint) : null
  if (a) {
    out.accent = a
    out.accentInk = inkOn(a)
  }
  if (m) {
    out.mint = m
    out.mintStrong = lighten(m, -0.18) === m ? m : darken(m, 0.16)
  }
  return Object.keys(out).length ? out : null
}

/** The other direction, for the strong bar. */
export function darken(hex: string, amount: number): string {
  const h = normalizeHex(hex)
  if (!h) return hex
  const ch = (i: number) => {
    const v = parseInt(h.slice(1 + i * 2, 3 + i * 2), 16)
    return Math.round(v * (1 - amount))
  }
  const to = (v: number) => v.toString(16).padStart(2, '0')
  return `#${to(ch(0))}${to(ch(1))}${to(ch(2))}`
}

/** Read what was chosen last time. Never throws: a broken value is no value. */
export function storedTheme(read: (k: string) => string | null): Partial<Theme> | null {
  try {
    const raw = read(KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<Theme>
    const out: Partial<Theme> = {}
    if (v.accent && normalizeHex(v.accent)) {
      out.accent = normalizeHex(v.accent)!
      out.accentInk = inkOn(out.accent)
    }
    if (v.mint && normalizeHex(v.mint)) {
      out.mint = normalizeHex(v.mint)!
      out.mintStrong = darken(out.mint, 0.16)
    }
    return Object.keys(out).length ? out : null
  } catch {
    return null
  }
}

export const THEME_KEY = KEY

/**
 * The custom properties this theme sets, and only these.
 *
 * They are named --theme-* rather than --action, because the workspace
 * declares --action on .ws and a token set on an element always beats one set
 * on its ancestor - the same rule that made two of today's color changes do
 * nothing at all. The stylesheet reads these with the real value as the
 * fallback, so with no theme set the app is exactly as it was.
 */
export function themeVars(t: Theme): Record<string, string> {
  return {
    '--theme-action': t.accent,
    '--theme-action-hover': darken(t.accent, 0.14),
    '--theme-action-ink': t.accentInk,
    '--theme-mint': t.mint,
    '--theme-mint-strong': t.mintStrong,
  }
}
